
/**
 * Utility for generating context-aware cooking tips
 */

import { generateBakingTip } from "./bakingTipGenerator";

/**
 * Generates recipe tips based on instruction context and recipe type, with ingredient awareness
 */
export const generateContextAwareTip = (
  instruction: string, 
  recipeType: string, 
  usedTips: Set<string>,
  recipeIngredients: string[] = []
): string | null => {
  // Get the base tip
  const tip = generateBakingTip(instruction);
  
  if (!tip) return null;
  
  // Convert ingredients to lowercase for easier matching
  const ingredientsLower = recipeIngredients.map(i => i.toLowerCase());
  
  // Filter out irrelevant tips based on ingredients
  const hasSugar = ingredientsLower.some(i => i.includes('sugar'));
  const hasButter = ingredientsLower.some(i => i.includes('butter'));
  const hasCream = ingredientsLower.some(i => i.includes('cream'));
  const isOvenRecipe = instruction.toLowerCase().includes('bake') || 
                       instruction.toLowerCase().includes('oven') ||
                       instruction.toLowerCase().includes('350');
  
  // Don't show baking-specific tips for recipes without relevant ingredients
  if (tip.includes('cream') && !hasCream && !instruction.toLowerCase().includes('cream')) {
    return null;
  }
  
  if (tip.includes('sugar') && !hasSugar && !instruction.toLowerCase().includes('sugar')) {
    return null;
  }
  
  if (tip.includes('butter') && !hasButter && !instruction.toLowerCase().includes('butter')) {
    return null;
  }
  
  // Don't show baking tips for non-baking recipes
  if (tip.includes('baking') && !isOvenRecipe) {
    return null;
  }
  
  // Don't show oven-specific tips for non-oven recipes
  if (tip.includes('oven door') && !isOvenRecipe) {
    return null;
  }
  
  // Don't use tips that are irrelevant to the recipe type
  if (tip.includes('tortilla') && recipeType !== 'tortilla') {
    return null;
  }
  
  // Don't use baking-specific tips for main dishes
  if ((tip.includes('cake') || tip.includes('cookie')) && recipeType === 'main_dish') {
    return null;
  }
  
  // Don't use folding tips unless the instruction involves folding
  if (tip.includes('fold') && !instruction.toLowerCase().includes('fold')) {
    return null;
  }
  
  // Avoid duplicate tips
  if (usedTips.has(tip)) {
    return null;
  }
  
  return tip;
};
